// hooks/useFtoForm.js
// Centralises all FTO form state, permission gating, and submission logic.
// Mirrors the ftoRequest_Load + fillRequest behaviour from the VB.NET source.

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAuth from "./useAuth";
import {
    getFtoRequest, getFtoBalances, getIndiaBalances, getDeptConflicts,
    submitFto, updateFto, updateFtoApprovals, getFtoDayCount,
} from "./ftoService";



// Go-live parity (2026-09-24), ATT#29: "today" is the Eastern (plant) date, not the
// UTC date (after 8 PM Eastern the UTC date is already tomorrow).
export const easternToday = () => {
    try {
        return new Intl.DateTimeFormat("en-CA", {
            timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit",
        }).format(new Date());
    } catch {
        return new Date().toISOString().split("T")[0];
    }
};
const today = () => easternToday();

// Go-live parity (2026-09-24), ATT#29: legacy rejected weekend start/end dates.
const isWeekend = (yyyyMmDd) => {
    if (!yyyyMmDd) return false;
    const [y, m, d] = yyyyMmDd.split("-").map(Number);
    const dow = new Date(y, m - 1, d).getDay();
    return dow === 0 || dow === 6;
};
const daysUntil = (yyyyMmDd) => {
    const [y, m, d] = yyyyMmDd.split("-").map(Number);
    const [ty, tm, td] = easternToday().split("-").map(Number);
    return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(ty, tm - 1, td)) / 86400000);
};
const currentYear = new Date().getFullYear();

// ─── Default form state ───────────────────────────────────────────────────────
const EMPTY_FTO = {
    empId: "",
    startDate: today(),
    endDate: "",
    startDayOption: "FULL",      // "FULL" | "FIRST_HALF" | "SECOND_HALF"
    endDayOption: "FULL",
    typeOfLeave: "SCHEDULED", // "SCHEDULED" | "COMPANY" | "OTHER"
    otherReason: "",
    // HR Round 4 / Issue 4 — independent of typeOfLeave.
    scheduledStatus: "Scheduled", // "Scheduled" | "Unscheduled" (call-out)
    payStatus: "Paid",            // "Paid" | "Unpaid"
    employeeComment: "",
    supervisorNote: "",
    hrNote: "",
    supervisorApproval: null,       // null=pending true=approved false=denied
    hrApproval: null,
    mpcApproval: null,
    exemption: false,
    ftoCount: 0,
    eflRequested: 0,
    evlRequested: 0,
};

const useFtoForm = ({ ftoId = 0 } = {}) => {
    const auth = useAuth();
    const navigate = useNavigate();
    const isNew = ftoId === 0;

    // ─── Form data ──────────────────────────────────────────────────────────────
    const [form, setForm] = useState({ ...EMPTY_FTO, empId: auth.empId });
    const [existingRequest, setExisting] = useState(null);
    const [balances, setBalances] = useState(null);
    const [indiaBalances, setIndiaBal] = useState(null);
    const [conflicts, setConflicts] = useState([]);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);

    // ─── Permission flags (mirrors VB.NET Load logic) ───────────────────────────
    // Employee can edit own new/existing request fields
    const isOwnRequest = form.empId === auth.empId;

    // Supervisor panel: only if logged-in user IS the employee's supervisor
    // Backend returns isSupervisorOf flag on the FTO record when fetched
    const [isSupervisorOf, setIsSupervisorOf] = useState(false);

    const canEditEmployeeFields = isNew
        ? true
        : isOwnRequest;

    const canEditSupervisor = !isNew && (auth.isSuperUser || isSupervisorOf);
    // Go-live parity (2026-09-24), ATT#17: the HR decision is HR / IT Admin only
    // (legacy: JCDept 19); the server enforces the same rule.
    const canEditHR = !isNew && (auth.isHR || auth.isItAdmin);  // dept 19 in legacy
    // Go-live parity (2026-09-24), ATT#16: MPC may record a decision on any request
    // (Round 4 locked decision; the server accepts MPC at any length).
    const canEditMPC = !isNew && (auth.isSuperUser || auth.isMPC || auth.isItAdmin);

    // ─── Load existing request ───────────────────────────────────────────────────
    useEffect(() => {
        if (isNew) {
            setForm((f) => ({ ...f, empId: auth.empId }));
            loadBalances(auth.empId);
            return;
        }
        (async () => {
            try {
                setLoading(true);
                const { data } = await getFtoRequest(ftoId);
                setExisting(data);
                setIsSupervisorOf(data.isSupervisorOf ?? false);  // backend sets this
                setForm({
                    empId: String(data.empId),
                    startDate: data.startDate?.split("T")[0] ?? "",
                    endDate: data.endDate?.split("T")[0] ?? "",
                    startDayOption: data.startDayOption ?? "FULL",
                    endDayOption: data.endDayOption ?? "FULL",
                    typeOfLeave: data.typeOfLeave ?? "SCHEDULED",
                    otherReason: data.otherReason ?? "",
                    scheduledStatus: data.scheduledStatus ?? "Scheduled",
                    payStatus: data.payStatus ?? "Paid",
                    employeeComment: data.employeeComment ?? "",
                    supervisorNote: data.supervisorNote ?? "",
                    hrNote: data.hrNote ?? "",
                    supervisorApproval: data.supervisorApproval ?? null,
                    hrApproval: data.hrApproval ?? null,
                    mpcApproval: data.mpcApproval ?? null,
                    exemption: data.exemption ?? false,
                    ftoCount: data.ftoCount ?? 0,
                    eflRequested: data.eflRequested ?? 0,
                    evlRequested: data.evlRequested ?? 0,
                });
                await loadBalances(String(data.empId));
            } catch (err) {
                toast.error("Failed to load FTO request.");
            } finally {
                setLoading(false);
            }
        })();
    }, [ftoId]);

    // ─── Load balances whenever empId or startDate changes ───────────────────────
    const loadBalances = useCallback(async (empId) => {
        if (!empId) return;
        try {

            const { data } = await getFtoBalances(empId, currentYear);
            setBalances(data);

        } catch {
            // non-fatal — balances panel just stays empty
        }
    }, [auth.isIndia]);

    // Go-live parity (2026-09-24), ATT#6: a supervisor may file a NEW request for
    // one of their active direct reports (DirectReportPicker). Balances and dept
    // conflicts follow the selected employee.
    const selectEmployee = useCallback((empId, empName) => {
        if (!empId) return;
        setForm((f) => ({ ...f, empId, empName: empId === auth.empId ? undefined : empName }));
        loadBalances(empId);
    }, [auth.empId, loadBalances]);

    // ─── Load dept conflicts only when startDate is set ──────────────────────
    useEffect(() => {
        if (!form.empId || !form.startDate) {
            setConflicts([]);
            return;
        }

        const timer = setTimeout(() => {
            getDeptConflicts(form.empId, form.startDate, form.endDate || undefined)
                .then(({ data }) => setConflicts(data ?? []))
                .catch(() => setConflicts([]));
        }, 400); // debounce — wait for user to finish picking dates

        return () => clearTimeout(timer);
    }, [form.empId, form.startDate, form.endDate]);

    // ─── Derived FTO count from current form state ───────────────────────────
    // Go-live parity (2026-09-24): the server's count is authoritative — it also
    // excludes holiday / shutdown dates (the local estimate below only skips
    // weekends and is shown until the server answers, or if it cannot).
    const [serverCount, setServerCount] = useState(null);
    useEffect(() => {
        setServerCount(null);
        if (!isNew || !form.startDate) return undefined;
        let cancelled = false;
        const timer = setTimeout(() => {
            getFtoDayCount({
                startDate: form.startDate,
                ...(form.endDate ? { endDate: form.endDate } : {}),
                startDayOption: form.startDayOption,
                endDayOption: form.endDayOption,
                typeOfLeave: form.typeOfLeave,
            })
                .then(({ data }) => {
                    if (!cancelled && typeof data?.days === "number") setServerCount(data.days);
                })
                .catch(() => { /* keep the local estimate */ });
        }, 300);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [isNew, form.startDate, form.endDate, form.startDayOption, form.endDayOption, form.typeOfLeave]);

    const calcFtoCount = useCallback(() => {
        // Go-live parity (2026-09-24), ATT#5: Company business / Other leave never
        // consume FTO (legacy counted them as 0 days).
        if (form.typeOfLeave === "COMPANY" || form.typeOfLeave === "OTHER") return 0;
        if (!form.startDate) return 1;

        if (!form.endDate) {
            return form.startDayOption === "FULL" ? 1 : 0.5;
        }

        const start = new Date(form.startDate);
        const end = new Date(form.endDate);
        if (end < start) return 0;

        let days = 0;
        const cur = new Date(start);
        while (cur <= end) {
            const dow = cur.getDay();
            if (dow !== 0 && dow !== 6) {        // skip weekends
                const isFirst = cur.toDateString() === start.toDateString();
                const isLast = cur.toDateString() === end.toDateString();
                if (isFirst && form.startDayOption !== "FULL") days += 0.5;
                else if (isLast && form.endDayOption !== "FULL") days += 0.5;
                else days += 1;
            }
            cur.setDate(cur.getDate() + 1);
        }
        return days;
    }, [form.typeOfLeave, form.startDate, form.endDate, form.startDayOption, form.endDayOption]);

    // ─── Derived balance numbers ──────────────────────────────────────────────
    const ftoCount = isNew ? (serverCount ?? calcFtoCount()) : form.ftoCount;

    const netAvailable = balances
        ? (balances.baseFtoEarned - balances.ftoDaysUsed)
        : 0;

    const balanceAfterRequest = balances
        ? (netAvailable - ftoCount - (balances.scheduledForFuture ?? 0))
        : 0;

    // ─── Field helpers ────────────────────────────────────────────────────────────
    const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

    // ─── Validation ───────────────────────────────────────────────────────────────
    const validate = () => {
        if (!form.startDate) return "Start date is required.";
        if (form.typeOfLeave === "OTHER" && !form.otherReason) return "Please select an other reason.";
        // Approvers deciding someone else's request don't edit the employee fields.
        if (!isNew && !isOwnRequest) return null;
        // Go-live parity (2026-09-24), ATT#29: legacy date rules (frmFtoRequest.vb:215-218,
        // 1054-1082) and comment rule (1129-1141, 1256-1262): a comment is required
        // only for less than 7 days' notice or Company business / Other leave.
        if (isWeekend(form.startDate)) return "The start date cannot be on a weekend.";
        if (form.endDate) {
            if (form.endDate < form.startDate) return "The end date cannot be before the start date.";
            if (isWeekend(form.endDate)) return "The end date cannot be on a weekend.";
        }
        const needsComment = form.typeOfLeave === "COMPANY" || form.typeOfLeave === "OTHER"
            || daysUntil(form.startDate) < 7;
        if (needsComment && !form.employeeComment.trim())
            return "A comment is required for Company business / Other leave, or with less than 7 days' notice.";
        return null;
    };

    // ─── Submit (new) ─────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        const err = validate();
        if (err) { toast.warning(err); return; }
        try {
            setSaving(true);

            const payload = {
                empId: form.empId,
                startDate: form.startDate,
                endDate: form.endDate || null,
                startDayOption: form.startDayOption,
                endDayOption: form.endDayOption,
                typeOfLeave: form.typeOfLeave,
                otherReason: form.otherReason || null,
                // HR Round 4 / Issue 4
                scheduledStatus: form.scheduledStatus,
                payStatus: form.payStatus,
                employeeComment: form.employeeComment,
            };

            if (isNew) {
                await submitFto(payload);
                // Go-live parity (2026-09-24), ATT#6: filed for a direct report.
                toast.success(form.empId !== auth.empId
                    ? "FTO request filed — your supervisor approval is recorded and HR has been notified."
                    : "FTO request submitted successfully.");
                navigate("/attendance");
            } else {
                if (isOwnRequest) {
                    await updateFto(ftoId, payload);
                }
                // Owner 2026-10-12 (Govind, 403 "Failed to save FTO request"): only someone
                // who may decide (supervisor, HR, MPC) sends the approvals; an employee
                // saving their own request has nothing to approve.
                if (canEditSupervisor || canEditHR || canEditMPC) await updateFtoApprovals(ftoId, {
                    supervisorNote: form.supervisorNote ?? "",
                    hrNote: form.hrNote ?? "",
                    supervisorApproval: form.supervisorApproval ?? null,
                    hrApproval: form.hrApproval ?? null,
                    mpcApproval: form.mpcApproval ?? null,
                    exemption: form.exemption ?? false,
                    // HR override (2026-07): record HR's decision as the
                    // supervisor decision too when the supervisor is
                    // unavailable (server only honors this for HR callers
                    // and only when no supervisor decision exists).
                    hrOverrideSupervisor: form.hrOverrideSupervisor ?? false,
                });
                toast.success("FTO request updated.");
                navigate("/attendance");
            }
        } catch (error) {
            console.error("❌ Status:", error?.response?.status);
            console.error("❌ Response:", JSON.stringify(error?.response?.data, null, 2));
            // Round 2c: show the server's actual reason (e.g. an overlap /
            // validation rejection) instead of a blind generic message.
            toast.error(
                error?.response?.data?.message
                || (typeof error?.response?.data === "string" ? error.response.data : null)
                || "Failed to save FTO request.");
        } finally {
            setSaving(false);
        }
    };

    return {
        form, setField,
        loading, saving,
        existingRequest,
        balances,
        netAvailable, balanceAfterRequest,
        ftoCount,          // expose so FtoForm can show "Days requested: X"
        conflicts,
        canEditEmployeeFields,
        canEditSupervisor,
        canEditHR,
        canEditMPC,
        isNew,
        auth,
        handleSubmit,
        selectEmployee,    // Go-live parity (2026-09-24), ATT#6
    };
};

export default useFtoForm;